import { useQuery } from '@tanstack/react-query'
import { dashboardAPI } from '../../api'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar,
} from 'recharts'
import {
  RiCalendarCheckLine, RiUserLine, RiMoneyDollarCircleLine,
  RiTimeLine, RiArrowUpLine, RiArrowDownLine,
} from 'react-icons/ri'
import { format } from 'date-fns'

function StatCard({ icon: Icon, label, value, sub, trend, color = 'primary' }) {
  const colors = {
    primary: 'bg-salon-primary/10 text-salon-primary',
    gold:    'bg-salon-secondary/10 text-salon-secondary',
    green:   'bg-green-100 text-green-600',
    purple:  'bg-purple-100 text-purple-600',
  }
  return (
    <div className="bg-white rounded-2xl p-6 border border-salon-cream-dark shadow-salon-sm">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${colors[color]}`}>
          <Icon className="text-xl" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs font-semibold font-body ${trend >= 0 ? 'text-green-600' : 'text-red-500'}`}>
            {trend >= 0 ? <RiArrowUpLine /> : <RiArrowDownLine />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <p className="font-display text-salon-dark text-2xl font-bold">{value}</p>
      <p className="text-salon-muted font-body text-sm mt-0.5">{label}</p>
      {sub && <p className="text-salon-muted/60 text-xs font-body mt-1">{sub}</p>}
    </div>
  )
}

export default function AdminDashboard() {
  const { data: stats } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => dashboardAPI.getStats(),
    select: (r) => r.data?.data,
  })

  const { data: revenue } = useQuery({
    queryKey: ['admin-revenue'],
    queryFn: () => dashboardAPI.getRevenue({ period: 'month' }),
    select: (r) => r.data?.data || [],
  })

  const { data: recent } = useQuery({
    queryKey: ['admin-recent'],
    queryFn: () => dashboardAPI.getRecent(),
    select: (r) => r.data?.data || [],
  })

  const STATUS_COLORS = {
    confirmed: 'badge-green',
    pending:   'badge-gold',
    cancelled: 'badge-red',
    completed: 'badge-primary',
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-salon-dark text-2xl font-bold">Dashboard Overview</h2>
        <p className="text-salon-muted font-body text-sm mt-0.5">{format(new Date(), 'EEEE, dd MMMM yyyy')}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={RiCalendarCheckLine} label="Today's Bookings"  value={stats?.today_bookings  || 0}  trend={12}  color="primary" />
        <StatCard icon={RiMoneyDollarCircleLine} label="Today's Revenue" value={`₹${(stats?.today_revenue || 0).toLocaleString()}`} trend={8} color="gold" />
        <StatCard icon={RiUserLine}             label="Total Customers" value={stats?.total_customers || 0}  trend={5}   color="green" />
        <StatCard icon={RiTimeLine}             label="Pending Approvals" value={stats?.pending || 0}       color="purple" />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-salon-cream-dark">
          <h3 className="font-semibold font-body text-salon-dark mb-4">Revenue (Last 30 Days)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={revenue || DEMO_REVENUE}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F4EBE3" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: 'DM Sans' }} />
              <YAxis tick={{ fontSize: 11, fontFamily: 'DM Sans' }} />
              <Tooltip contentStyle={{ fontFamily: 'DM Sans', borderRadius: 12, border: '1px solid #F4EBE3' }} />
              <Line type="monotone" dataKey="amount" stroke="#8B1A4A" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-salon-cream-dark">
          <h3 className="font-semibold font-body text-salon-dark mb-4">Bookings by Service</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={stats?.service_breakdown || DEMO_SERVICES} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#F4EBE3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fontFamily: 'DM Sans' }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fontFamily: 'DM Sans' }} width={80} />
              <Tooltip contentStyle={{ fontFamily: 'DM Sans', borderRadius: 12 }} />
              <Bar dataKey="count" fill="#C9963A" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent bookings */}
      <div className="bg-white rounded-2xl border border-salon-cream-dark overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-salon-cream-dark">
          <h3 className="font-semibold font-body text-salon-dark">Recent Appointments</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-body">
            <thead>
              <tr className="bg-salon-cream text-salon-muted text-xs font-semibold uppercase tracking-wider">
                <th className="text-left px-6 py-3">Customer</th>
                <th className="text-left px-6 py-3">Service</th>
                <th className="text-left px-6 py-3">Date & Time</th>
                <th className="text-left px-6 py-3">Staff</th>
                <th className="text-left px-6 py-3">Amount</th>
                <th className="text-left px-6 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-salon-cream-dark">
              {(recent?.length ? recent : DEMO_RECENT).map((a) => (
                <tr key={a.id} className="hover:bg-salon-cream/50 transition-colors">
                  <td className="px-6 py-3.5">
                    <div>
                      <p className="font-medium text-salon-dark">{a.customer_name}</p>
                      <p className="text-xs text-salon-muted">{a.customer_phone}</p>
                    </div>
                  </td>
                  <td className="px-6 py-3.5 text-salon-dark">{a.services?.[0]?.name || a.service}</td>
                  <td className="px-6 py-3.5">
                    <p className="text-salon-dark">{a.date}</p>
                    <p className="text-xs text-salon-muted">{a.time}</p>
                  </td>
                  <td className="px-6 py-3.5 text-salon-dark">{a.staff?.name || a.staff_name || '–'}</td>
                  <td className="px-6 py-3.5 text-salon-secondary font-semibold">₹{(a.total || 0).toLocaleString()}</td>
                  <td className="px-6 py-3.5">
                    <span className={`badge ${STATUS_COLORS[a.status] || 'badge-primary'} capitalize`}>
                      {a.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

const DEMO_REVENUE   = Array.from({ length: 14 }, (_, i) => ({ date: `${i + 1}`, amount: Math.floor(Math.random() * 8000 + 4000) }))
const DEMO_SERVICES  = [
  { name: 'Hair Color',  count: 42 },
  { name: 'Haircut',     count: 78 },
  { name: 'Facial',      count: 35 },
  { name: 'Bridal',      count: 12 },
  { name: 'Nail Art',    count: 26 },
]
const DEMO_RECENT = [
  { id: 1, customer_name: 'Priya Sharma',   customer_phone: '98765 43210', service: 'Hair Coloring', date: 'Today',     time: '11:00 AM', staff_name: 'Anjali', total: 2500, status: 'confirmed' },
  { id: 2, customer_name: 'Anjali Kumar',   customer_phone: '87654 32109', service: 'Haircut',       date: 'Today',     time: '2:30 PM',  staff_name: 'Meera',  total: 800,  status: 'pending' },
  { id: 3, customer_name: 'Roshni Pillai',  customer_phone: '76543 21098', service: 'Facial',        date: 'Tomorrow',  time: '10:00 AM', staff_name: 'Anjali', total: 1500, status: 'confirmed' },
  { id: 4, customer_name: 'Deepika Nair',   customer_phone: '65432 10987', service: 'Bridal Makeup', date: '10 May',    time: '8:00 AM',  staff_name: 'Sara',   total: 8000, status: 'confirmed' },
  { id: 5, customer_name: 'Kavitha Reddy',  customer_phone: '54321 09876', service: 'Nail Art',      date: 'Yesterday', time: '4:00 PM',  staff_name: 'Meera',  total: 600,  status: 'completed' },
]
